import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { EmptyState, Modal, PageHead, Skeleton } from '../../components/ui/common';
import { Icon } from '../../components/ui/icon';
import { toast } from '../../components/ui/toast';
import { api, errorMessage } from '../../lib/api';
import { useCan } from '../../lib/session';
import { pickChannel, qk, useChannel, useChannels, useChannelStatusLive, waChannels, type ChannelDetail, type ChannelDto } from './api';
import { ConnectionTab } from './connection-tab';
import { formatPhone } from './format';
import { AdvancedTab, LimitsTab, ProfileTab, RestrictionsTab } from './channel-tabs';
import { ChannelStatusPill, InlineError } from './ui';
import './b2a.css';

export const WA_TABS = ['connection', 'profile', 'limits', 'restrictions', 'advanced'] as const;
export type WaTab = (typeof WA_TABS)[number];

const TAB_LABEL: Record<WaTab, string> = {
  connection: 'חיבור',
  profile: 'פרופיל',
  limits: 'מכסה וקצב',
  restrictions: 'הגבלות וואטסאפ',
  advanced: 'מתקדם',
};

const LEADS: Record<WaTab, (c: ChannelDto) => string> = {
  connection: (c) =>
    c.status === 'ready' || c.status === 'restricted'
      ? 'המספר מקושר ל-OpenWA כמכשיר נוסף. כל ההודעות ללקוחות יוצאות ממנו.'
      : 'המספר לא מקושר כרגע. ההודעות ממתינות בתור ויישלחו אחרי הסריקה.',
  profile: () => 'השם, הטקסט והתמונה שהלקוחות רואים.',
  limits: () => 'כמה הודעות יוצאות ובאיזה קצב, כדי שהמספר לא ייחסם.',
  restrictions: () => 'אם וואטסאפ הגביל את המספר, ומה קורה אז.',
  advanced: () => 'מנוע, גרסה, webhook, הפעלה מחדש וניתוק.',
};

/** D-150: one card per WhatsApp number (each opens its own tabs with ?channel=) + "הוספת מספר". */
export function NumbersList({ numbers, currentId, onAdd }: { numbers: ChannelDto[]; currentId: string; onAdd?: () => void }) {
  return (
    // a nav of links (not role=list/listitem, which would hide the links and the button from assistive tech)
    <nav className={`wa2-nums${numbers.length > 1 ? ' many' : ''}`} aria-label="מספרים">
      {numbers.map((c) => {
        const on = c.id === currentId;
        return (
          <Link
            key={c.id}
            className={`wa2-num${on ? ' on' : ''}`}
            to="/whatsapp"
            search={{ channel: numbers.length > 1 ? c.id : undefined }}
            aria-current={on ? 'true' : undefined}
          >
            <span className="wa2-num-ic">
              <Icon name="phone" />
            </span>
            <span className="grow">
              <b>{c.name}</b>
              <span className="ph ltr">{formatPhone(c.phone)}</span>
              <small>{c.roles.includes('client_alerts') ? "התראות וסיכומים לקבוצות של הלקוחות, וצ'אטים איתם" : 'מספר נוסף'}</small>
            </span>
            <ChannelStatusPill channel={c} />
          </Link>
        );
      })}
      {onAdd ? (
        <button type="button" className="wa2-num add" onClick={onAdd}>
          <span className="wa2-num-ic">
            <Icon name="plus" />
          </span>
          <span className="grow">
            <b>הוספת מספר</b>
            <small>מספר וואטסאפ נוסף לקבוצות של לקוחות. מחברים אותו בסריקת QR או בקוד צימוד</small>
          </span>
        </button>
      ) : null}
    </nav>
  );
}

/** Creates a WhatsApp number (role client_alerts); used by the first-number screen and by "הוספת מספר". */
function useCreateChannel(onCreated?: (c: ChannelDto) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api<ChannelDto>('/api/channels', { body: { name: name.trim(), roles: ['client_alerts'] } }),
    onSuccess: (c) => {
      toast('המספר נוצר. עכשיו סורקים QR כדי לקשר אותו');
      void qc.invalidateQueries({ queryKey: qk.channels });
      void qc.invalidateQueries({ queryKey: ['statusbar'] });
      onCreated?.(c);
    },
    onError: (e) => {
      toast(errorMessage(e), 'error');
      void qc.invalidateQueries({ queryKey: qk.channels });
    },
  });
}

/** D-150 "הוספת מספר": name it, then the new number's connection tab (QR / pairing code) opens. */
export function AddChannelModal({ defaultName, onClose }: { defaultName: string; onClose: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState(defaultName);
  const create = useCreateChannel((c) => {
    onClose();
    void navigate({ to: '/whatsapp', search: { channel: c.id } });
  });
  const submit = () => {
    if (name.trim() && !create.isPending) create.mutate(name);
  };
  return (
    <Modal
      open
      title="הוספת מספר"
      onClose={onClose}
      footer={
        <>
          <button className="btn primary" type="button" disabled={!name.trim() || create.isPending} onClick={submit}>
            <Icon name="plus" />
            {create.isPending ? 'יוצרים…' : 'יצירת המספר'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            ביטול
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="field">
          <label htmlFor="wa2-add-name">שם המספר</label>
          <input className="input" id="wa2-add-name" autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} />
          <span className="help">אחרי היצירה נפתח מסך החיבור של המספר: סורקים QR מהטלפון שלו, או מקלידים קוד צימוד.</span>
        </div>
      </form>
      <div className="callout info mt">
        <Icon name="info" />
        <div>מספר חדש שולח מעט בשבועות הראשונים. מעלים את התקרה היומית בהדרגה בלשונית "מכסה וקצב".</div>
      </div>
    </Modal>
  );
}

function tabDot(tab: WaTab, c: ChannelDto) {
  if (tab === 'connection' && c.status !== 'ready' && c.status !== 'restricted') {
    const danger = c.status === 'disconnected' || c.status === 'failed';
    return <span className={`status-dot ${danger ? 'danger' : 'warn'}`} />;
  }
  if (tab === 'restrictions' && (c.status === 'restricted' || Boolean(c.restriction))) return <span className="status-dot danger" />;
  return null;
}

/** First number: shown when no channel exists yet (fresh install). */
function CreateChannel() {
  const can = useCan();
  const [name, setName] = useState('מספר הלקוחות');
  const create = useCreateChannel();
  return (
    <EmptyState
      icon="phone"
      title="עוד לא חובר מספר"
      text="יוצרים את מספר הלקוחות במערכת, ואז סורקים QR מהטלפון שלו. כל ההתראות והסיכומים ללקוחות יוצאים ממנו."
      action={
        can('channels.connect') ? (
          <form
            className="row"
            style={{ justifyContent: 'center' }}
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim()) create.mutate(name);
            }}
          >
            <label className="sr-only" htmlFor="wa2-new-name">
              שם המספר
            </label>
            <input className="input" id="wa2-new-name" style={{ maxWidth: 220 }} value={name} onChange={(e) => setName(e.target.value)} />
            <button className="btn primary" disabled={!name.trim() || create.isPending}>
              <Icon name="plus" />
              {create.isPending ? 'יוצרים…' : 'יצירת המספר'}
            </button>
          </form>
        ) : (
          <p className="small muted">רק מי שמורשה לחבר מספרים יכול ליצור אותו.</p>
        )
      }
    />
  );
}

/** D-150: `channelId` = the `?channel=` search of the route (a WhatsApp number; unknown -> the main number). */
export function WhatsAppPage({ tab, channelId }: { tab: WaTab; channelId?: string }) {
  useChannelStatusLive();
  const can = useCan();
  const list = useChannels();
  const numbers = waChannels(list.data);
  const main = pickChannel(list.data, channelId);
  const detail = useChannel(main?.id);
  const tabsRef = useRef<HTMLElement>(null);
  const [adding, setAdding] = useState(false);
  // the tabs keep the chosen number; with one number the URLs stay as before
  const search = { channel: numbers.length > 1 && main ? main.id : undefined };

  useEffect(() => {
    tabsRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [tab]);

  const head = (
    <PageHead title="מספרים" lead={numbers.length > 1 ? 'המספרים שמחוברים למערכת.' : 'המספרים שמחוברים למערכת. כרגע מחובר מספר אחד, מספר הלקוחות.'} />
  );

  if (list.isPending) return <Skeleton />;
  if (list.isError)
    return (
      <>
        {head}
        <InlineError text={`לא הצלחנו לטעון את המספרים. ${errorMessage(list.error)}`} onRetry={() => void list.refetch()} />
      </>
    );
  if (!main)
    return (
      <>
        {head}
        <CreateChannel />
      </>
    );

  // detail (with events) when loaded; the list row is enough for the frame meanwhile
  const channel: ChannelDetail = detail.data ?? { ...main, events: [] };

  return (
    <>
      {head}
      <NumbersList numbers={numbers} currentId={channel.id} onAdd={can('channels.connect') ? () => setAdding(true) : undefined} />
      <nav className="tabs" aria-label={numbers.length > 1 ? `הגדרות המספר ${channel.name}` : 'הגדרות המספר'} ref={tabsRef}>
        {WA_TABS.map((t) =>
          t === 'connection' ? (
            <Link key={t} to="/whatsapp" search={search} activeOptions={{ exact: true }} aria-current={tab === t ? 'page' : undefined}>
              {TAB_LABEL[t]}
              {tabDot(t, channel)}
            </Link>
          ) : (
            <Link key={t} to="/whatsapp/$tab" params={{ tab: t }} search={search} aria-current={tab === t ? 'page' : undefined}>
              {TAB_LABEL[t]}
              {tabDot(t, channel)}
            </Link>
          ),
        )}
      </nav>
      {adding ? <AddChannelModal defaultName={`מספר ${numbers.length + 1}`} onClose={() => setAdding(false)} /> : null}
      <p className="muted small mb">{LEADS[tab](channel)}</p>
      {tab === 'connection' ? <ConnectionTab channel={channel} eventsLoading={detail.isPending} /> : null}
      {tab === 'profile' ? <ProfileTab channel={channel} /> : null}
      {tab === 'limits' ? <LimitsTab channel={channel} /> : null}
      {tab === 'restrictions' ? <RestrictionsTab channel={channel} /> : null}
      {tab === 'advanced' ? <AdvancedTab channel={channel} /> : null}
    </>
  );
}
